import { DOCUMENT } from '@angular/common'
import { Inject, Injectable, OnDestroy } from '@angular/core'
import { Router } from '@angular/router'
import { EventService } from './event.service'
import { Observable } from 'rxjs'
import { map } from 'rxjs/operators'
import { SubSink } from 'subsink'
import { ApiService } from './api.service'
import { FirestoreService2 } from './firestore.service2'
import { GlobalService } from './global.service'
import { timeOut, use } from './utils'

export enum AutomationStatus {
  SCHEDULED = 'SCHEDULED',
  COMPLETE = 'COMPLETE',
  ERROR = 'ERROR',
  RECURRENT = 'RECURRENT',
  PERMANENT = 'PERMANENT',
  FRONTEND = 'FRONTEND'
}

export interface IAutomation {
  active: boolean
  conditions: string[] // array with the names of the Conditions functions in the backend
  description: string
  event?: string
  hour?: number
  minute?: number
  name: string
  options: any[] // array with objects to be passed to Conditions and Workers
  // performAt?: { nanoseconds: number; seconds: number }; // standard firebase server timestamp
  performAt?: string // standard firebase server timestamp
  refId?: string // id of the object that generated this automation
  collection?: string // collection where the object that generated the automation is
  status: AutomationStatus
  updatedAt?: string
  updatedBy?: string
  weekDay?: number
  workers: string[] // array with the names of the Workers functions in the backend
}

@Injectable({
  providedIn: 'root'
})
export class AutomationsService implements OnDestroy {

  private subs = new SubSink()
  private automationsFrontend: any[] = []

  constructor(
    private fss: FirestoreService2,
    private g: GlobalService,
    private router: Router,
    @Inject(DOCUMENT) private document: Document) {
    console.info(`## ${this.constructor.name}`)
    this.subs.add(
      this.fss.list('automations', { where: [['status', '==', AutomationStatus.FRONTEND], ['active', '==', false]] }).subscribe(automations => this.automationsFrontend = automations)
    )
    this.listenEvents()
  }

  ngOnDestroy(): void {
    this.subs.unsubscribe()
  }

  /**
   * Returns the TYPES (status) of available automations
   * @returns {(AutomationStatus.RECURRENT | AutomationStatus.PERMANENT)[]}
   */
  get automationTypes() {
    return [AutomationStatus.RECURRENT, AutomationStatus.PERMANENT]
  }

  /**
   * Returns an Array of strings with the names of available events in the backend
   * @returns {string[]}
   */
  get events() {
    return [
      'first_login',
      'new_register',
      'new_prospect',
      'new_post',
      'new_challenge',
      'answer_challenge'
    ]
  }

  /**
   * Returns an Array of strings with the names of available events in the frontend
   * @returns {string[]}
   */
  get frontendEvents() {
    return [
      'on_login',
      'first_login',
      'enter_app',
      'new_register',
      'new_post'
    ]
  }

  /**
   * Returns an Array of strings with the names of available Workers functions in the backend
   * @returns {string[]}
   */
  get workers() {
    return [
      'sendEmailToUsers',
      'sendSmsToUsers',
      'sendPushToUsers',
      // 'applyPointsToUser',
      // 'applyPointsToSegment'
      'executeOneFunction',
      'executeScript',
      'callUrl'
    ]
  }

  /**
   * Returns an Array of strings with the names of available Workers functions in the backend
   * @returns {string[]}
   */
  get frontendWorkers() {
    return [
      // 'openModal',
      'navigateTo',
      'executeScript'
    ]
  }

  /**
   * Returns an Array of strings with the names of available Conditions functions in the backend
   * @returns {string[]}
   */
  get conditions() {
    return [
      'event_occurred',
      'all_users_logged_in',
      'all_users_not_logged_in',
      'all_users_segment_logged_in',
      'all_users_segment_not_logged_in',
      'all_active_users',
      'all_users',
      'all_influencers',
      'by_user_id',
      'by_segment_id',
      'by_object_id',
      'all_users_with_challenge_not_completed',
      'all_users_with_anonymous_survey_not_completed',
      'all_influencers_with_challenge_not_completed'
    ]
  }

  /**
   * Returns the list of parameters of a Worker function
   * @param {string} worker
   * @returns {any}
   */
  getWorkersParams(worker: string) {
    const params = {
      sendEmailToUsers: ['templateId', 'text', 'subject'],
      sendSmsToUsers: ['text'],
      sendPushToUsers: ['title', 'text'],
      executeOneFunction: ['functionName'],
      executeScript: ['script'],
      navigateTo: ['path'],
    }
    return params[worker] || []
  }

  /**
   * Returns the list of parameters of a Conditions function
   * @param {string} condition
   * @returns {any}
   */
  getConditionsParams(condition: string) {
    const params = {
      all_users_segment_logged_in: ['segment'],
      all_users_segment_not_logged_in: ['segment'],
      by_user_id: ['id'],
      by_segment_id: ['id'],
      by_object_id: ['id', 'collection'],
      all_users_with_challenge_not_completed: ['challengeId'],
      all_users_with_anonymous_survey_not_completed: ['challengeId'],
      all_influencers_with_challenge_not_completed: ['challengeId'],
    }
    return params[condition] || []
  }

  /**
   * Lists the RECURRENT and PERMANENT automations registered for the current nick
   * By default, lists only active automations
   * Pass FALSE as parameter, returns the list with active and inactive
   * @param {boolean} onlyActives
   * @returns {Observable<any[]>}
   */
  listAutomations(onlyActives = true): Observable<any[]> {
    const where: any[] = [['status', 'in', [AutomationStatus.RECURRENT, AutomationStatus.PERMANENT, AutomationStatus.FRONTEND]]]
    if (onlyActives) where.push(['active', '==', true])
    return this.fss.list('automations', { where })
      .pipe(map((values: any[]) => values.map(v => ({ ...v, _actions: (v.workers || []).length }))))
  }

  /**
   * Lists the automations for a specific reference
   * @param {boolean} onlyActives
   * @returns {Observable<any[]>}
   */
  listAutomationsByRef(refId: string, collection: string): Observable<any[]> {
    return this.fss.list('automations', { where: [['refId', '==', refId], ['collection', '==', collection]] })
      .pipe(map((values: any[]) => values
        .filter(v => v.type !== 'bulk')
        .map(v => ({ ...v, _sendAt: v.performAt/*this.fbTimestampToDate(v.performAt).toISOString()*/, _actions: (v.workers || []).length }))
        .sort((a, b) => a._sendAt === b._sendAt ? 0 : a._sendAt < b._sendAt ? -1 : 1)))
  }

  /**
   * Saves an automation
   * If an ID is provided, the automation will be created with this ID or updated if it exists
   * @param {IAutomation} automation
   * @param {string} id
   * @returns {Promise<any>}
   */
  registerAutomation(automation: Partial<IAutomation>, id: string = null): Promise<any> {
    return this.fss.merge('automations', id || this.fss.createId, automation)
  }

  /**
   * Dispatches an automation event that will be captured by the backend
   * @param {string} event
   * @param data
   * @param delay
   * @returns {Observable<any>}
   */
  dispatchEvent(event: string, data?: any, delay = 0) {
    timeOut(() => {
      EventService.get(event).emit(data)
      const api = use<ApiService>(ApiService)
      this.subs.add(
        api.post('cron/registerEvent?noError=1', { event, data: data || null }).subscribe(res => null)
      )
    }, delay)
  }

  onEvent(event: string): Observable<any> {
    return EventService.get(event)
  }

  /**
   * Converts a JavaScript date to a Firebase Server Timestamp
   * @param {Date} date
   * @returns {{seconds: number, nanoseconds: number}}
   */
  dateToFBTimestamp(date: Date) {
    return {
      nanoseconds: 0,
      seconds: date.getTime() / 1000
    }
  }

  /**
   * Converts the fb.server.timestamp to a JavaScript Date object
   * @param timestamp
   * @returns {Date}
   */
  fbTimestampToDate(timestamp: any) {
    return new Date(timestamp.seconds * 1000)
  }

  runAutomation() {
    const api = use<ApiService>(ApiService)
    api.get('cron').subscribe(() => console.log('Automation run OK'))
  }

  private listenEvents() {
    for (const evt of this.frontendEvents) {
      this.subs.add(EventService.get(evt).subscribe(e => {
        for (const aut of this.automationsFrontend) {
          if (aut.event === evt) {
            for (const [i, work] of aut.workers.entries()) {
              switch (work) {
                case 'navigateTo':
                  this.g.set('fullscreen', true)
                  this.router.navigate([aut.options[i].path])
                  break
                case 'executeScript':
                  // tslint:disable-next-line:no-eval
                  eval(aut.options[i].script)
              }
            }
          }
        }
      }))
    }
  }
}
